/** The day and the time shown (Sprint 103.0, mockup 5). */

import type { ReactNode } from 'react';
import {
  parseLocalDate,
  seasonDates,
  seasonOf,
  type LocalDate,
  type LocalTime,
  type SolarZone
} from '@archisimple/solar';
import type { Day, Place } from '../model/sky.ts';
import { useWords } from './common.tsx';
import { SunStrip } from './SunStrip.tsx';

export function DateSheet({
  place,
  zone,
  today,
  localDate,
  localTime,
  day,
  playing,
  showReferences,
  zoneLine,
  onDate,
  onTime,
  onPlay,
  onReferences,
  onClose
}: {
  readonly place: Place;
  readonly zone: SolarZone;
  readonly today: LocalDate;
  readonly localDate: LocalDate;
  readonly localTime: LocalTime;
  readonly day: Day;
  readonly playing: boolean;
  readonly showReferences: boolean;
  readonly zoneLine: string;
  readonly onDate: (date: LocalDate) => void;
  readonly onTime: (time: LocalTime) => void;
  readonly onPlay: () => void;
  readonly onReferences: (show: boolean) => void;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useWords();
  const year = Number(localDate.slice(0, 4));
  const presets = [
    { key: 'today', label: t('preset.today'), date: today },
    ...seasonDates(year, zone).map((season) => ({
      key: season.season,
      label: t(`preset.${seasonOf(season.season, place.latitude)}`),
      date: season.localDate
    }))
  ];

  return (
    <section className="sheet" aria-labelledby="date-title">
      <div className="sheet__handle" />
      <div className="row">
        <h1 id="date-title" style={{ margin: 0, fontSize: '1.0625rem' }}>
          {t('date.title')}
        </h1>
        <div className="spacer" />
        <button type="button" className="btn btn--primary" onClick={onClose}>
          {t('common.done')}
        </button>
      </div>
      <p className="section" style={{ marginTop: 0 }}>
        {t('date.days')}
      </p>
      <div className="presets">
        {presets.map((preset) => (
          <button
            key={preset.key}
            type="button"
            className="btn btn--pill"
            aria-pressed={preset.date === localDate}
            onClick={() => onDate(preset.date)}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className="field">
        <label htmlFor="date">{t('date.date')}</label>
        <input
          id="date"
          className="input"
          type="date"
          value={localDate}
          onChange={(event) => {
            const parsed = parseLocalDate(event.target.value);
            if (parsed.ok) onDate(parsed.value);
          }}
        />
      </div>
      <SunStrip day={day} localTime={localTime} playing={playing} onTime={onTime} onPlay={onPlay} />
      <label className="switch">
        <input
          type="checkbox"
          checked={showReferences}
          onChange={(event) => onReferences(event.target.checked)}
        />
        <span>{t('date.compare')}</span>
      </label>
      <p className="note">{zoneLine}</p>
    </section>
  );
}
